import { Ref, forwardRef } from "react"
import * as yup from "yup"
import { Form, Formik, FormikProps } from "formik"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CreateUpdatePaymentLinkFormContent } from "./CreateUpdatePaymentLinkFormContent"
import { PaymentLinkItem, CreateUpdatePaymentLinkFormValues } from "../paymentLinksTypes"

type CreateUpdatePaymentLinkFormProps = Readonly<{
    initialValues: CreateUpdatePaymentLinkFormValues
    onSubmit: (formData: PaymentLinkItem) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdatePaymentLinkFormValues>>

export const CreateUpdatePaymentLinkForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdatePaymentLinkFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const validationSchema = yup.object({
            title: yup.string().required(l10n.getString("required-field")),
            description: yup.string().required(l10n.getString("required-field")),
            shop: yup.object({
                id: yup.number().required(l10n.getString("required-field")).typeError(l10n.getString("required-field")),
            }),

            salesAccount: yup.object({
                id: yup.number().required(l10n.getString("required-field")).typeError(l10n.getString("required-field")),
            }),
            availabilityDetails: yup.object({
                dateInterval: yup
                    .object({
                        from: yup.mixed().nullable(),
                        to: yup.mixed().nullable(),
                    })
                    .nullable()
                    .test(
                        "dateInterval",
                        l10n.getString("payment-links-form-availability-error-picker"),
                        function (value) {
                            const { from, to } = value || {}
                            if ((from && !to) || (!from && to)) {
                                return false
                            }
                            return true
                        },
                    ),
            }),
            security: yup.object({
                hasPasswordProtection: yup.boolean(),
                password: yup.string().when("hasPasswordProtection", {
                    is: (val: boolean) => val === true,
                    then: (schema) => schema.required(l10n.getString("required-field")),
                    otherwise: (schema) => schema.nullable(),
                }),
            }),
        })

        function handleSubmit(values: CreateUpdatePaymentLinkFormValues) {
            const { availabilityDetails, shop, salesAccount, ...restValues } = values

            const modifyData = {
                ...restValues,
                shop: { id: Number(shop?.id) },
                salesAccount: { id: Number(salesAccount?.id) },
                availabilityDetails: {
                    ...(availabilityDetails?.dateInterval
                        ? {
                              dateInterval: {
                                  ...(availabilityDetails?.dateInterval?.from
                                      ? { from: availabilityDetails?.dateInterval?.from.toISODate() }
                                      : {}),
                                  ...(availabilityDetails?.dateInterval?.to
                                      ? { to: availabilityDetails?.dateInterval?.to.toISODate() }
                                      : {}),
                              },
                          }
                        : {}),
                },
            }
            onSubmit(modifyData)
        }

        return (
            <>
                <Formik<CreateUpdatePaymentLinkFormValues>
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <CreateUpdatePaymentLinkFormContent />
                    </Form>
                </Formik>
            </>
        )
    },
)
